import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { LOCAL_STATUS_COMPLETED_OFFLINE, LOCAL_STATUS_COMPLETION_ATTENTION } from "../field_sync_shared"

// Supplements the Today tab with live stats and schedule data.
//
// Online: fetches fresh stat counts from /field/today/stats after Turbo
// restores a cached page, so the ice cream graph reflects state changes
// (completions, starts) made on other pages without a full reload.
//
// Offline: reads cached_responses from IndexedDB to both patch missing
// place cards and recalculate the stat counts. Only activates offline for
// the place-patching logic; stat refresh runs in both modes.

// Same groups, labels and order as the server-rendered list
// (app/views/field/today/index.html.erb). "completed" added for card #4516 --
// WIN1 parity: a place whose only today activity has ended stays visible
// offline too, instead of disappearing once its last response is submitted.
const GROUP_ORDER  = ["started", "overdue", "scheduled", "completed"]
const GROUP_LABELS = { started: "In Progress", overdue: "Overdue", scheduled: "Scheduled Today", completed: "Completed" }

// Card #4594 -- cached_responses.status can now hold two local-only overlay
// values (field_sync_shared.js) that never come from the server: a
// completion queued while offline reads "completed_offline" here until sync
// confirms it, and one that got rejected/gave up retrying reads
// "completion_attention". Every place below that groups/counts by
// cached_responses.status must read THIS instead of the raw field, or a
// response simply vanishes from every bucket (todo/scheduled/started/done)
// the moment either overlay is set -- it has to count as something.
// "completed_offline" counts as done (the tasker finished it); unresolved
// "completion_attention" counts as still started (it needs the tasker back,
// not a quiet "done").
function effectiveStatus(cachedResponse) {
  if (cachedResponse.status === LOCAL_STATUS_COMPLETED_OFFLINE) return "ended"
  if (cachedResponse.status === LOCAL_STATUS_COMPLETION_ATTENTION) return "started"
  return cachedResponse.status
}

export default class extends Controller {
  static targets = ["list", "statCount"]

  async connect() {
    if (navigator.onLine) {
      // Refresh stats from the server in the background. This is a no-op on
      // first load (server-rendered counts are already correct) but fixes
      // stale counts when Turbo restores the page from its cache.
      this._refreshStatsFromServer()
    } else {
      await this._patchFromIndexedDB()
      await this._updateStatsFromIndexedDB()
    }
  }

  // -- Online: fetch fresh counts from server -----------------------------------

  async _refreshStatsFromServer() {
    try {
      const res = await fetch("/field/today/stats", {
        headers: { "Accept": "application/json" }
      })
      if (!res.ok) return
      const stats = await res.json()
      this._applyStats(stats)
    } catch {
      // Network failure -- leave server-rendered counts unchanged
    }
  }

  // -- Offline: patch place list + recalculate counts from IndexedDB -----------

  async _patchFromIndexedDB() {
    const now = new Date()
    const todayEnd = new Date(now)
    todayEnd.setHours(23, 59, 59, 999)

    // Find responses scheduled for today or overdue, started responses, and
    // ended responses (card #4516 -- a response completed offline is written
    // straight to "ended" locally; prefetch never caches an already-ended
    // response, so any "ended" row here is necessarily from today).
    const responses = await db.cached_responses.toArray()
    const relevant = responses.filter(r => {
      const status = effectiveStatus(r)
      if (status === "started" || status === "ended") return true
      if (status === "scheduled" && r.scheduledAt) {
        return new Date(r.scheduledAt) <= todayEnd
      }
      return false
    })

    if (relevant.length === 0) return

    // Group by placeId
    const byPlace = {}
    for (const r of relevant) {
      if (!r.placeId) continue
      if (!byPlace[r.placeId]) byPlace[r.placeId] = []
      byPlace[r.placeId].push(r)
    }

    // Find place IDs already rendered in the server HTML
    const renderedLinks = this.hasListTarget
      ? this.listTarget.querySelectorAll("a[href*='/field/places/']")
      : document.querySelectorAll("a[href*='/field/places/']")

    const renderedIds = new Set()
    renderedLinks.forEach(a => {
      const match = a.href.match(/\/field\/places\/(\d+)/)
      if (match) renderedIds.add(parseInt(match[1]))
    })

    // Inject cards for any missing places, under the same group headings the
    // server list uses (card #3827 -- they used to be prepended bare).
    const container = this.hasListTarget ? this.listTarget : null
    if (!container) return

    const cardsByGroup = { started: [], overdue: [], scheduled: [], completed: [] }
    for (const [placeIdStr, placeResponses] of Object.entries(byPlace)) {
      const placeId = parseInt(placeIdStr)
      if (renderedIds.has(placeId)) continue

      const place = await db.cached_places.get(placeId)
      const placeName = place?.name || `Place #${placeId}`
      const group = this._groupFor(placeResponses, now)

      const card = document.createElement("div")
      card.setAttribute("data-idb-place-id", placeId)
      // Card #4516 -- keeps an offline-completed card out of
      // field_today_sort_controller.js's drag-to-reorder, same as a
      // server-rendered one.
      if (group === "completed") card.setAttribute("data-field-today-completed", "true")
      card.innerHTML = this._buildCard(placeId, placeName, placeResponses, now)
      cardsByGroup[group].push(card)
    }

    for (const group of GROUP_ORDER) {
      if (cardsByGroup[group].length > 0) this._insertIntoGroup(container, group, cardsByGroup[group])
    }
  }

  // Same rule as the server list: any started response puts the place In
  // Progress; otherwise any scheduled before today makes it Overdue; otherwise
  // any still-scheduled response makes it Scheduled Today; a place left with
  // only ended responses is Completed (card #4516).
  _groupFor(responses, now) {
    if (responses.some(r => effectiveStatus(r) === "started")) return "started"
    const startOfToday = new Date(now.toDateString())
    if (responses.some(r => effectiveStatus(r) === "scheduled" && r.scheduledAt && new Date(r.scheduledAt) < startOfToday)) return "overdue"
    if (responses.some(r => effectiveStatus(r) === "scheduled")) return "scheduled"
    return "completed"
  }

  // Cards go straight after their group's heading when the server already
  // rendered it (no duplicate heading). Otherwise a heading is added, placed
  // before the next group's heading, else after the last place row, else at
  // the top.
  _insertIntoGroup(container, group, cards) {
    const existing = container.querySelector(`[data-field-today-group="${group}"]`)
    if (existing) {
      existing.after(...cards)
      return
    }

    const heading = document.createElement("p")
    heading.className = "text-xs font-semibold text-slate-400 uppercase tracking-wide pt-2 pb-0.5"
    heading.dataset.fieldTodayGroup = group
    heading.textContent = GROUP_LABELS[group]

    const laterGroups = GROUP_ORDER.slice(GROUP_ORDER.indexOf(group) + 1)
    const nextHeading = laterGroups
      .map(g => container.querySelector(`[data-field-today-group="${g}"]`))
      .find(Boolean)
    const rows = container.querySelectorAll(":scope > [data-place-id], :scope > [data-idb-place-id]")

    if (nextHeading) {
      nextHeading.before(heading, ...cards)
    } else if (rows.length > 0) {
      rows[rows.length - 1].after(heading, ...cards)
    } else {
      container.prepend(heading, ...cards)
    }
  }

  async _updateStatsFromIndexedDB() {
    const now = new Date()
    const todayEnd = new Date(now)
    todayEnd.setHours(23, 59, 59, 999)

    const responses = await db.cached_responses.toArray()

    // Responses relevant to today's list
    const todayResponses = responses.filter(r => {
      const status = effectiveStatus(r)
      if (status === "started" || status === "ended") return true
      if (status === "scheduled" && r.scheduledAt) {
        return new Date(r.scheduledAt) <= todayEnd
      }
      return false
    })

    const placeIds = new Set(todayResponses.map(r => r.placeId).filter(Boolean))

    // All responses for places appearing in today's list (for todo count)
    const allForTodayPlaces = responses.filter(r => placeIds.has(r.placeId))

    const stats = {
      todo:      allForTodayPlaces.filter(r => effectiveStatus(r) === "todo").length,
      scheduled: todayResponses.filter(r => effectiveStatus(r) === "scheduled").length,
      started:   todayResponses.filter(r => effectiveStatus(r) === "started").length,
      completed: todayResponses.filter(r => effectiveStatus(r) === "ended").length
    }

    this._applyStats(stats)
  }

  _applyStats(stats) {
    this.statCountTargets.forEach(el => {
      const key = el.dataset.statKey
      if (key && stats[key] !== undefined) {
        el.textContent = stats[key]
      }
    })
  }

  // -- Card builder for IDB-only places ----------------------------------------

  _buildCard(placeId, placeName, responses, now) {
    const hasStarted   = responses.some(r => effectiveStatus(r) === "started")
    const scheduledAt  = responses.map(r => r.scheduledAt).filter(Boolean).sort()[0]
    const isOverdue    = scheduledAt && new Date(scheduledAt) < new Date(now.toDateString())

    const dateLabel = scheduledAt
      ? new Date(scheduledAt).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
      : null

    const dateHtml = dateLabel
      ? `<p class="text-xs mt-0.5 ${isOverdue ? "text-amber-600" : "text-slate-400"}">${isOverdue ? "Due " : ""}${dateLabel}</p>`
      : ""

    const startedCount   = responses.filter(r => effectiveStatus(r) === "started").length
    const scheduledCount = responses.filter(r => effectiveStatus(r) === "scheduled").length

    return `
      <a href="/field/places/${placeId}" class="block bg-white rounded-xl border border-amber-200 p-4 shadow-sm active:bg-slate-50 mb-2">
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-slate-800 leading-snug">${this._esc(placeName)}</p>
            ${dateHtml}
          </div>
          <span class="text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full shrink-0">Offline</span>
        </div>
      </a>
    `
  }

  _esc(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
